import {
    IContextualMenuProps,
    IContextualMenuItem,
} from 'office-ui-fabric-react/lib/components/ContextualMenu';
import { isGroupTableQuery } from 'owa-group-utils';
import { ActionSource, ClientItem } from 'owa-mail-store';
import { listViewStore, TableView, MailRowDataPropertyGetter } from 'owa-mail-list-store';
import getRetentionPolicySubMenuProps from './getRetentionPolicySubMenuProps';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getFolderIdFromTableView } from 'owa-mail-search';
import { getFolderTable } from 'owa-folders';
import {
    ARCHIVE_INHERIT_SUBMENU_ITEM_IDENTIFIER,
    DELETE_INHERIT_SUBMENU_ITEM_IDENTIFIER,
} from './retentionPolicyConstants';
import { getRetentionPolicyTagList } from './getRetentionPolicyTagList';
import RetentionTagType from 'owa-service/lib/contract/RetentionTagType';
import isSelectedPolicyDifferentForItem from '../helpers/isSelectedPolicyDifferentForItem';
import {
    onRetentionTagForItemsChanged,
    onRetentionTagForConversationsChanged,
} from '../actions/internalActions';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import {
    getTableItemRelation,
    getTableConversationRelation,
    getApplyConversationActionContext,
} from 'owa-mail-list-store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';

/**
 * Utility to get contextual menu props
 * @param tableViewId table view id
 * @param actionSource source of invocation of action
 * @param item the exact item for getting archive and policy tag (mainly neede in item header drop down button)
 */
export default function getAssignPolicyPropertiesForMailContextMenu(
    tableViewId: string,
    actionSource: ActionSource,
    item?: ClientItem
): IContextualMenuProps {
    const tableView: TableView = listViewStore.tableViews.get(tableViewId);
    if (!tableView) {
        return null;
    }

    const retentionPolicyTagList = getRetentionPolicyTagList(tableView);
    if (!retentionPolicyTagList || !retentionPolicyTagList.length) {
        return null;
    }

    const folderInfo = getFolderInfoForRetentionPolicy(tableView);
    if (!folderInfo) {
        return null;
    }

    const selectedRowKeys = [...tableView.selectedRowKeys.keys()];
    let selectedArchiveTag;
    let selectedDeleteTag;
    let showTagSelection;
    if (item) {
        selectedArchiveTag = item.ArchiveTag;
        selectedDeleteTag = item.PolicyTag;
        showTagSelection = true;
    } else {
        selectedArchiveTag =
            selectedRowKeys.length == 1
                ? MailRowDataPropertyGetter.getArchiveTag(selectedRowKeys[0], tableView)
                : null;
        selectedDeleteTag =
            selectedRowKeys.length == 1
                ? MailRowDataPropertyGetter.getPolicyTag(selectedRowKeys[0], tableView)
                : null;
        showTagSelection = selectedRowKeys.length == 1;
    }

    return getRetentionPolicySubMenuProps({
        retentionPolicyTagList: retentionPolicyTagList,
        showTagSelection: showTagSelection,
        selectedArchiveTag: selectedArchiveTag,
        selectedDeleteTag: selectedDeleteTag,
        treeType: folderInfo.treeType,
        distinguishedFolderId: 'none',
        hasArchive: getIsArchiveEnabled(tableView),
        onArchivePolicyClickedCallback: onPolicyClicked({
            isArchivePolicyClicked: true,
            tableView: tableView,
            selectedTag: selectedArchiveTag,
            folderId: folderInfo.folderId,
            clientItem: item,
            actionSource: actionSource,
        }),
        onDeletePolicyClickedCallback: onPolicyClicked({
            isArchivePolicyClicked: false,
            tableView: tableView,
            selectedTag: selectedDeleteTag,
            folderId: folderInfo.folderId,
            clientItem: item,
            actionSource: actionSource,
        }),
    });
}

function getFolderInfoForRetentionPolicy(tableView: TableView) {
    let folderId;
    let treeType;
    if (tableView && isGroupTableQuery(tableView.tableQuery)) {
        folderId = null; // No folder id needs to be set for groups
        treeType = 'groups';
    } else {
        const tableViewFolderId = getFolderIdFromTableView(tableView);
        const folder = tableViewFolderId && getFolderTable().get(tableViewFolderId);
        if (!folder) {
            return null;
        }

        folderId = folder.DistinguishedFolderId || folder.FolderId.Id;
        treeType = folder.treeType;
    }

    return { folderId: folderId, treeType: treeType };
}

function getIsArchiveEnabled(tableView: TableView): boolean {
    if (tableView && isGroupTableQuery(tableView.tableQuery)) {
        return false;
    }

    return !!getUserConfiguration().SessionSettings.HasArchive;
}

function onPolicyClicked(config: {
    isArchivePolicyClicked: boolean;
    tableView: TableView;
    selectedTag: RetentionTagType;
    folderId: string;
    clientItem: ClientItem;
    actionSource: ActionSource;
}) {
    const {
        isArchivePolicyClicked,
        tableView,
        selectedTag,
        folderId,
        clientItem,
        actionSource,
    } = config;
    return (
        ev: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item: IContextualMenuItem
    ) => {
        const isSelectedPolicyDifferent = isSelectedPolicyDifferentForItem({
            retentionTag: selectedTag,
            retentionId: item.data,
            inheritSubMenuIdentifier: isArchivePolicyClicked
                ? ARCHIVE_INHERIT_SUBMENU_ITEM_IDENTIFIER
                : DELETE_INHERIT_SUBMENU_ITEM_IDENTIFIER,
        });
        if (!isSelectedPolicyDifferent) {
            return;
        }
        // Check if the action is done upon an item or conversation and act accordingly
        const isPolicyChangedForItem =
            actionSource == 'RPCSAOverflow' ||
            tableView.tableQuery.listViewType === ReactListViewType.Message;
        const selectedRowKeys = [...tableView.selectedRowKeys.keys()];
        const mailboxInfo = getMailboxInfo(tableView);
        if (isPolicyChangedForItem) {
            const itemIds =
                actionSource == 'RPCSAOverflow'
                    ? [clientItem.ItemId.Id]
                    : selectedRowKeys.map(
                          rowKey => getTableItemRelation(rowKey, tableView.id).clientId.Id
                      );
            onRetentionTagForItemsChanged(
                isArchivePolicyClicked,
                item.data,
                itemIds,
                actionSource,
                mailboxInfo
            );
        } else {
            const conversationActionContexts = selectedRowKeys.map(rowKey =>
                getApplyConversationActionContext(rowKey, tableView.id)
            );
            const itemIds: string[] = selectedRowKeys.reduce((acc, rowKey) => {
                const tableConversationRelation = getTableConversationRelation(
                    rowKey,
                    tableView.id
                );
                if (tableConversationRelation) {
                    acc.push(...tableConversationRelation.itemIds);
                }
                return acc;
            }, []);
            onRetentionTagForConversationsChanged(
                isArchivePolicyClicked,
                item.data,
                folderId,
                conversationActionContexts,
                itemIds,
                actionSource,
                mailboxInfo
            );
        }
    };
}
